import { createContext, ReactNode, useContext, useMemo, useReducer, useState } from "react";
import { ReassignUserContextState } from "../types/context";
import { ItemNameEnum, LoadingNameEnum, ReassignUserAction, ReassignUserTabs } from "../enums";
import { useReassignColumn, useTrackingColumn } from "../hooks";
import { ressignUserReducer } from "../reducer";
import { removeAllReference } from "../../../../../../../utils/methods/removedReference";
import { reassignUserState } from "../constants";
import { getArea, getDepartments, getDivisions, getRoles, getStore, getUserTypes } from "../../../../../../../utils/methods/geServiceWithItemsFormat";
import { reportFolioServices, userServiceFetch } from "../../main/constants/states";
import { formatUsersToReassign, formatUsersToTracking } from "../formaters";
import { ReassignUserBody, TrackingPayload } from "../../../../../../../service/reportFolio/body";
import { SnackbarFormat } from "../../../../../../../utils/types/generals";
import { Roles, Types } from "../../../../../../../utils/enums";
import { handleAllErrors } from "../../../../../../../utils/methods/handleErrors";
import { useTranslation } from "react-i18next";
import { Query } from "../../../typing";
import { returnQuery } from "../../../../../../../utils/methods/returnQuery";
import { getLocalStorage } from "../../../../../../../utils/crud/crypt";
import { useNavigate } from "react-router-dom";

export const ReassignUserContext = createContext<ReassignUserContextState>(Object({}))
export const useReassignUserContext = () => useContext(ReassignUserContext)

export const ReassigUserProvider = ({ children }: { children: ReactNode }) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const staleLocale = getLocalStorage()
    const [state, dispatch] = useReducer(ressignUserReducer, removeAllReference(reassignUserState))
    const { trackingData, trackingRow } = state
    const [tabValue, setTabValue] = useState(ReassignUserTabs.REASSIGN)
    const allUsersSelecteds = useMemo(() => {
        if (!trackingRow?.length) return false
        return !trackingRow.some(userTracking => !trackingData.usersToTracking.includes(userTracking.id))
    }, [trackingData.usersToTracking, trackingRow])
    const columnReassign = useReassignColumn({ userToReassignId: state.userToReassignId, selectUser: ({ id }) => { dispatch({ payload: { userId: id }, type: ReassignUserAction.CHANGE_REASSIGN_USER_ID }) } })
    const columnTracking = useTrackingColumn({
        allUsersSelecteds,
        userToTracking: state.trackingData.usersToTracking,
        selectUser: ({ id }) => { dispatch({ payload: { userId: id }, type: ReassignUserAction.SELECT_USER }) },
        selectUserAll: () => { dispatch({ type: ReassignUserAction.SELECT_ALL_USER }) },
    })
    const [snackbar, setSnackbar] = useState<SnackbarFormat>({ open: false, message: "", type: Types.SUCCESS })
    const changeTab = ({ value }: { value: ReassignUserTabs }) => { setTabValue(value) }

    const getAreaItems = async () => {
        if (!state.items.area?.length) {
            const item = await getArea()
            dispatch({ type: ReassignUserAction.UPDATE_ITEM, payload: { item, name: ItemNameEnum.AREA } })
        }
    }

    const getRoleItems = async () => {
        if (!state.items.role?.length) {
            const skip = staleLocale.role != Roles.ADMIN ? [Roles.ADMIN] : []
            const { items } = await getRoles({ all: true, skip })
            dispatch({ type: ReassignUserAction.UPDATE_ITEM, payload: { item: items, name: ItemNameEnum.ROLE } })
        }
    }
    const getDivisionItems = async () => {
        if (!state.items.division?.length) {
            const { items } = await getDivisions()
            dispatch({ type: ReassignUserAction.UPDATE_ITEM, payload: { item: items, name: ItemNameEnum.DIVISION } })
        }
    }
    const getUserTypeItems = async () => {
        if (!state.items.userType?.length) {
            const { items } = await getUserTypes({ all: true })
            dispatch({ type: ReassignUserAction.UPDATE_ITEM, payload: { item: items, name: ItemNameEnum.USERTYPE } })
        }
    }
    const getDepartmentItems = async () => {
        if (!state.items.department?.length) {
            const items = await getDepartments()
            dispatch({ type: ReassignUserAction.UPDATE_ITEM, payload: { item: items, name: ItemNameEnum.DEPARTMENT } })
        }
    }
    const getStoreItems = async () => {
        if (!state.items.store?.length) {
            const { items } = await getStore()
            dispatch({ type: ReassignUserAction.UPDATE_ITEM, payload: { item: items, name: ItemNameEnum.STORE } })
        }
    }

    const getUsersToReassign = async ({ id }: { id?: string }) => {
        if (!id || !!state.reassignRow.length) { return }
        dispatch({ type: ReassignUserAction.LOADING, payload: { value: true, name: LoadingNameEnum.REASSIGN_TABLE } })
        const users = await userServiceFetch.usersFolio(id)
        dispatch({ type: ReassignUserAction.FILL_REASSIGN_USER, payload: { row: formatUsersToReassign({ users }) } })
        dispatch({ type: ReassignUserAction.LOADING, payload: { value: false, name: LoadingNameEnum.REASSIGN_TABLE } })
    }

    const reassignUser = async ({ id }: { id?: string }) => {
        if (!id || !state.userToReassignId) {
            return;
        }
        dispatch({
            type: ReassignUserAction.LOADING,
            payload: { value: true, name: LoadingNameEnum.REASSIGN_SAVE },
        });
        const body: ReassignUserBody = { userManageId: state.userToReassignId };
        const response = await reportFolioServices.reassignReportFolio({ body, id });
        const errorCode = response?.errors?.[0]?.code;
        dispatch({
            type: ReassignUserAction.LOADING,
            payload: { value: false, name: LoadingNameEnum.REASSIGN_SAVE },
        });
        if (errorCode) {
            setSnackbar({
                message: handleAllErrors(errorCode, t),
                open: true,
                type: Types.ERROR,
            });
            return;
        }
        setSnackbar({
            open: true,
            message: t('reportFolio.detail.reassignUser.message.edit'),
            type: Types.SUCCESS,
        });

        setTimeout(() => {
            navigate(`/dashboard/report-folio/dashboard/detail/${id}`);
        }, 3000);
    };


    const searchUsersToTracking = async ({ query }: { query: Query }) => {
        const companyId = staleLocale?.company?.id
        const queryCompany = companyId ? `company_id=${companyId}&` : ''
        dispatch({ type: ReassignUserAction.LOADING, payload: { value: true, name: LoadingNameEnum.TRACKING_TABLE } })
        const users = await userServiceFetch.filterUsers({ query: `${queryCompany}status_ids=1&${returnQuery(query, {})}` })
        dispatch({ type: ReassignUserAction.FILL_TRACKING_USER, payload: { row: formatUsersToTracking({ users, oldUsersList: state.trackingRow, usersSelecteds: state.trackingData.usersToTracking }) } })
        dispatch({ type: ReassignUserAction.LOADING, payload: { value: false, name: LoadingNameEnum.TRACKING_TABLE } })
    }
    const checkBossCanFollowUp = () => { dispatch({ type: ReassignUserAction.CHECK_CAN_BOSS_FOLLOW_UP }) }
    const closeSnackbar = () => { setSnackbar(prev => ({ ...prev, open: false })) }
    const saveTracking = async ({ id }: { id?: string }) => {
        if (!id) { return }
        dispatch({ type: ReassignUserAction.LOADING, payload: { value: true, name: LoadingNameEnum.TRACKING_SAVE } })
        const body: TrackingPayload['body'] = { directSupervisors: !!trackingData.bossCanFollowUp, userIds: trackingData.usersToTracking }
        const payload: TrackingPayload = { id, body }
        const response = await reportFolioServices.trackingnReportFolio(payload)
        const errorCode = response?.errors?.[0]?.code
        dispatch({ type: ReassignUserAction.LOADING, payload: { value: false, name: LoadingNameEnum.TRACKING_SAVE } })
        if (errorCode) {
            setSnackbar({ message: handleAllErrors(errorCode, t,), open: true, type: Types.ERROR })
            return
        }
        setSnackbar({
            open: true,
            message: t('reportFolio.tracking.snackbar.success'),
            type: Types.SUCCESS
        })
    }

    return (
        <ReassignUserContext.Provider
            value={{
                changeTab,
                closeSnackbar,
                checkBossCanFollowUp,
                state,
                columnReassign,
                tabValue,
                columnTracking,
                snackbar,
                services: {
                    getAreaItems,
                    getDepartmentItems,
                    getDivisionItems,
                    getRoleItems,
                    getStoreItems,
                    getUsersToReassign,
                    getUserTypeItems,
                    reassignUser,
                    searchUsersToTracking,
                    saveTracking,
                }
            }}
        >
            {children}
        </ReassignUserContext.Provider>
    )
}